// frontend/src/components/Layout/Layout.jsx
import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import CookieConsent from "../Common/CookieConsent";
import TenantHelmet from "../Common/TenantHelmet";
import SupportChatWidget from "../Common/SupportChatWidget";
import StorefrontPromoBanner from "../../utils/storefrontpromobanner";
import { useTenant } from "../../context/TenantContext";
import {
  getStorefrontThemeConfig,
  STOREFRONT_TEMPLATE_IDS,
} from "../../utils/storefrontTheme";
import "../../styles/navbar.css";
import "../../styles/footer.css";
import "../../styles/storefront-templates.css";

const Layout = () => {
  const location = useLocation();
  const { settings, tenant } = useTenant();
  const storefrontTemplate = STOREFRONT_TEMPLATE_IDS.includes(
    settings?.storefrontTemplate,
  )
    ? settings.storefrontTemplate
    : "classic";
  const themeConfig = getStorefrontThemeConfig(
    storefrontTemplate,
    {
      ...(storefrontTemplate === "classic"
        ? {
            primaryColor: tenant?.PrimaryColor,
            accentColor: tenant?.SecondaryColor,
          }
        : {}),
      ...(settings?.storefrontThemeConfig || {}),
    },
  );
  const mobileLayout = themeConfig.storefrontContent.mobileLayout;
  const getThemeColor = (key, fallback) =>
    /^#[0-9a-fA-F]{6}$/.test(themeConfig[key] || "")
      ? themeConfig[key]
      : fallback;
  const headerStyle = ["light", "dark", "brand"].includes(
    themeConfig.headerStyle,
  )
    ? themeConfig.headerStyle
    : storefrontTemplate === "marketplace"
      ? "dark"
      : "light";
  const footerStyle = ["light", "dark", "brand"].includes(
    themeConfig.footerStyle,
  )
    ? themeConfig.footerStyle
    : storefrontTemplate === "marketplace"
      ? "dark"
      : storefrontTemplate === "minimal"
        ? "light"
        : "brand";
  const themeVariables = {
    "--primary-color": getThemeColor("primaryColor", "#2c7da0"),
    "--secondary-color": getThemeColor("accentColor", "#61a5c2"),
    "--store-accent": getThemeColor("accentColor", "#61a5c2"),
    "--store-page-bg": getThemeColor("pageBackground", "#f8fafc"),
    "--store-surface": getThemeColor("surfaceColor", "#ffffff"),
    "--store-text": getThemeColor("textColor", "#1e293b"),
    "--store-content-width":
      themeConfig.contentWidth === "wide" ? "1480px" : "1280px",
    "--store-radius":
      themeConfig.cornerStyle === "sharp"
        ? "3px"
        : themeConfig.cornerStyle === "rounded"
          ? "18px"
          : "10px",
    "--store-font-family":
      themeConfig.fontFamily === "serif"
        ? "'Georgia', 'Times New Roman', serif"
        : "inherit",
    "--store-header-bg":
      getThemeColor(
        "navBackgroundColor",
        headerStyle === "dark"
          ? "#17212b"
          : headerStyle === "brand"
            ? getThemeColor("primaryColor", "#2c7da0")
            : "#ffffff",
      ),
    "--store-header-text":
      getThemeColor(
        "navLinkColor",
        headerStyle === "light" ? "#202a32" : "#ffffff",
      ),
    "--store-footer-bg":
      footerStyle === "dark"
        ? "#17212b"
        : footerStyle === "brand"
          ? storefrontTemplate === "classic" &&
            !settings?.storefrontThemeConfig?.primaryColor
            ? tenant?.HeaderFooterColor ||
              getThemeColor("primaryColor", "#2c7da0")
            : getThemeColor("primaryColor", "#2c7da0")
          : "#ffffff",
    "--store-footer-text": footerStyle === "light" ? "#202a32" : "#ffffff",
    "--storefront-mobile-product-gap":
      mobileLayout.productSpacing === "compact"
        ? "8px"
        : mobileLayout.productSpacing === "spacious"
          ? "20px"
          : "14px",
  };

  // Hide footer on dashboard and admin pages
  const shouldHideFooter =
    location.pathname === "/dashboard" ||
    location.pathname.startsWith("/admin");

  // Hide cookie consent on admin pages
  const shouldHideCookieConsent = location.pathname.startsWith("/admin");

  return (
    <div
      className={`layout storefront-template-${storefrontTemplate}`}
      style={themeVariables}
      data-mobile-product-spacing={mobileLayout.productSpacing}
      data-mobile-card-style={mobileLayout.cardStyle}
    >
      <TenantHelmet />
      {!location.pathname.startsWith("/admin") && (
        <>
          <StorefrontPromoBanner placement="TopBar" />
          <StorefrontPromoBanner placement="Popup" />
        </>
      )}
      <Navbar />
      <main className="main-content">
        <Outlet />
      </main>
      {!shouldHideFooter && <Footer />}
      {!shouldHideCookieConsent && <CookieConsent />}
      {!location.pathname.startsWith("/admin") && <SupportChatWidget />}
    </div>
  );
};

export default Layout;
